/**
 * The HTML frame design system's runtime: tabs, filters and charts a page
 * gets from `data-*` attributes alone, so they work in a trusted-scripts-only
 * frame too. It is injected into every frame document and into a downloaded
 * page. It builds nodes with createElement and textContent, never posts to
 * the app and never clicks for the user: host actions stay with the bridge
 * (`html-frame-runtime.ts`), gated on a real click and confirmed by the app.
 *
 *   <div class="tabs" data-tabs>
 *     <div class="tab-list"><button data-tab="a">A</button>…</div>
 *     <section data-tab-panel="a">…</section>…</div>
 *   <div class="filters" data-filter-group="q"><button data-filter="all">All</button>
 *     <button data-filter="late">Late</button>
 *     <input class="search" type="search" data-filter-search="q" aria-label="Search"></div>
 *   <table data-filter-list="q">… <tr data-tags="late">…</table>
 *   <p class="empty" data-filter-empty="q">…</p> <small data-filter-count="q"></small>
 */
import { CHART_RUNTIME } from "./html-frame-chart-runtime";

export const HTML_FRAME_DESIGN_SCRIPT = `<script data-denker-runtime="design">
(function(){
${CHART_RUNTIME}
  var tabGroups=0;
  function initTabs(root){
    if(root.__denkerTabs)return;
    root.__denkerTabs=true;
    function own(selector){return Array.prototype.filter.call(root.querySelectorAll(selector),function(el){return el.closest("[data-tabs]")===root;});}
    var tabs=own("[data-tab]"),panels=own("[data-tab-panel]"),id="denker-tabs-"+(++tabGroups);
    if(!tabs.length)return;
    if(tabs[0].parentElement)tabs[0].parentElement.setAttribute("role","tablist");
    function select(name,focus){
      tabs.forEach(function(tab){
        var on=tab.getAttribute("data-tab")===name;
        tab.setAttribute("aria-selected",on?"true":"false");
        tab.tabIndex=on?0:-1;
        if(on&&focus)tab.focus();
      });
      panels.forEach(function(panel){panel.hidden=panel.getAttribute("data-tab-panel")!==name;});
    }
    tabs.forEach(function(tab,i){
      var name=tab.getAttribute("data-tab");
      tab.setAttribute("role","tab");
      if(tab.tagName==="BUTTON"&&!tab.hasAttribute("type"))tab.setAttribute("type","button");
      if(!tab.id)tab.id=id+"-tab-"+i;
      panels.forEach(function(panel,j){
        if(panel.getAttribute("data-tab-panel")!==name)return;
        if(!panel.id)panel.id=id+"-panel-"+j;
        panel.setAttribute("role","tabpanel");
        panel.setAttribute("aria-labelledby",tab.id);
        tab.setAttribute("aria-controls",panel.id);
      });
      tab.addEventListener("click",function(){select(name,false);});
      tab.addEventListener("keydown",function(event){
        if(event.altKey||event.ctrlKey||event.metaKey||event.shiftKey)return;
        var n=tabs.length,next={ArrowRight:i+1,ArrowLeft:i-1+n,Home:0,End:n-1}[event.key];
        if(next===undefined)return;
        event.preventDefault();
        select(tabs[next%n].getAttribute("data-tab"),true);
      });
    });
    var first=tabs.filter(function(tab){return tab.getAttribute("aria-selected")==="true";})[0]||tabs[0];
    select(first.getAttribute("data-tab"),false);
  }
  var filters=Object.create(null);
  function filterState(name){return filters[name]||(filters[name]={tag:"",text:""});}
  function named(attribute,name){return Array.prototype.filter.call(document.querySelectorAll("["+attribute+"]"),function(el){return el.getAttribute(attribute)===name;});}
  function applyFilter(name){
    var state=filterState(name),shown=0,total=0;
    named("data-filter-list",name).forEach(function(list){
      list.querySelectorAll("[data-tags]").forEach(function(item){
        var tags=(item.getAttribute("data-tags")||"").split(/\\s+/);
        var match=(!state.tag||tags.indexOf(state.tag)!==-1)&&(!state.text||item.textContent.toLowerCase().indexOf(state.text)!==-1);
        item.hidden=!match;
        total++;
        if(match)shown++;
      });
    });
    named("data-filter-empty",name).forEach(function(el){el.hidden=shown>0;});
    named("data-filter-count",name).forEach(function(el){el.textContent=shown===total?String(total):shown+" / "+total;});
  }
  function initFilterGroup(group){
    if(group.__denkerFilters)return;
    group.__denkerFilters=true;
    var name=group.getAttribute("data-filter-group"),buttons=group.querySelectorAll("[data-filter]");
    function tagOf(button){var value=button.getAttribute("data-filter")||"";return value==="all"?"":value;}
    function press(tag){
      filterState(name).tag=tag;
      buttons.forEach(function(button){button.setAttribute("aria-pressed",tagOf(button)===tag?"true":"false");});
      applyFilter(name);
    }
    buttons.forEach(function(button){
      if(button.tagName==="BUTTON"&&!button.hasAttribute("type"))button.setAttribute("type","button");
      button.addEventListener("click",function(){press(tagOf(button));});
    });
    var pressed=group.querySelector('[data-filter][aria-pressed="true"]');
    press(pressed?tagOf(pressed):"");
  }
  function initSearch(input){
    if(input.__denkerSearch)return;
    input.__denkerSearch=true;
    var name=input.getAttribute("data-filter-search");
    input.addEventListener("input",function(){filterState(name).text=String(input.value||"").trim().toLowerCase();applyFilter(name);});
  }
  function init(){
    if(!document.body)return;
    renderCharts();
    document.querySelectorAll("[data-tabs]").forEach(initTabs);
    document.querySelectorAll("[data-filter-group]").forEach(initFilterGroup);
    document.querySelectorAll("[data-filter-search]").forEach(initSearch);
    document.querySelectorAll("[data-filter-list]").forEach(function(list){applyFilter(list.getAttribute("data-filter-list"));});
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",init,{once:true});
  else init();
})();
</scr${""}ipt>`;
